import React from "react"
import type { UserInfo } from "#/lib/carrot-api/types/UserInfo"
import useMemberQuery from "#/services/user/member-info"

// The consumer subscription funnel's public surface, plus the signed-in member's
// carrot-app profile that its details step prefills from.
//
// The profile is read with `useMemberQuery` — `getCurrentUser` on the shared `["member"]`
// react-query key — rather than threaded down from redux `userInfo`. It is the SAME
// endpoint either way (redux's own `fetchUserInfo` calls `getCurrentUser` too), but this
// keeps the funnel's host contract off the `state.userInfo` shape and reads through
// `UserInfo`, which is typed where the `(state: any) => any` selectors were not.
//
// It lives HERE, in the host, rather than inside `@internal/consumer`: the package never
// reaches into `#/…`, so the host resolves session data and hands it in as props — the
// same rule `isLoggedIn`, `email` and `stripePublishableKey` already follow.
//
// The lazy import stays in this file so the package is still code-split: nothing from
// `@internal/consumer` loads until someone navigates to a `/consumer` path.
const ConsumerSubscriptionRoute = React.lazy(() =>
  import("@internal/consumer").then((m) => ({ default: m.ConsumerSubscriptionRoute }))
)

type ConsumerSubscriptionRouteProps = {
  isLoggedIn: boolean
  email?: string
  stripePublishableKey?: string
  selfServeSignupEnabled?: boolean
  onLogin?: (email: string, password: string) => Promise<void>
}

/**
 * The funnel's prefill fields, carrot-app-native: `state` is the 2-letter USPS
 * abbreviation and `dateOfBirth` is "YYYY-MM-DD". `UserInfo` nulls each optional field
 * while the funnel's contract takes `?: string`, so nulls are mapped to undefined —
 * otherwise a `null` would override the funnel's own value in its `funnel ?? profile`
 * prefill chain instead of falling through to it.
 */
function selectFunnelProfile(user: UserInfo) {
  return {
    firstName: user.firstName,
    lastName: user.lastName,
    dateOfBirth: user.dateOfBirth ?? undefined,
    state: user.state ?? undefined,
    zip: user.zip ?? undefined,
    // Gender identity as carrot-app stores it — the same enum values the funnel's own
    // option list uses, so it prefills verbatim. `genderIdentityOther` is the free-text
    // detail that accompanies ANOTHER_GENDER_IDENTITY.
    genderIdentity: user.genderIdentity ?? undefined,
    genderIdentityOther: user.genderIdentityOther ?? undefined
  }
}

function SignedInConsumerSubscriptionRoute(props: ConsumerSubscriptionRouteProps): JSX.Element {
  // `data` is undefined until the query resolves, which the funnel already treats as
  // "no profile to prefill from" — the same state redux left it in while user info was
  // still loading.
  const { data: profile } = useMemberQuery(selectFunnelProfile)
  return <ConsumerSubscriptionRoute {...props} profile={profile} />
}

export function ConsumerSubscriptionRouteWithProfile(props: ConsumerSubscriptionRouteProps): JSX.Element {
  // `getCurrentUser` is an AUTHENTICATED endpoint and this route is mounted for signed-out
  // visitors too (the funnel is public), so the query has to sit in a child that only
  // mounts once there is a session: `useMemberQuery` takes no `enabled` option, and a hook
  // can't be called conditionally. Without the split, every anonymous funnel visit would
  // fire a request that can only 401.
  return props.isLoggedIn ? <SignedInConsumerSubscriptionRoute {...props} /> : <ConsumerSubscriptionRoute {...props} />
}
